import { lazy, Suspense } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { formatRef, parseRef } from '@promanage/core';
import {
  isAreaColouring,
  isHeatMetric,
  isPropertyMapFilter,
  isPropertyMapView,
  type AreaColouring,
  type HeatMetric,
  type PropertyMapFilter,
  type PropertyMapView,
} from '../features/property/mapFilter';

const PropertyMapPage = lazy(async () => {
  const module = await import('../features/property/PropertyMapPage');
  return { default: module.PropertyMapPage };
});

/**
 * What the map is showing, as the URL carries it.
 *
 * `q` matches the transactions page — it is what people type, and short enough to survive being
 * pasted into a chat window. `show` rather than `filter`, because `filter` reads as a verb in a
 * URL and this names a view.
 */
interface PropertyMapSearch {
  q?: string;
  show?: PropertyMapFilter;
  /**
   * Whose properties are highlighted, as their staff ref — `?manager=US000003`.
   *
   * A REF, NOT A UUID. This is the half of the URL a person reads, bookmarks and pastes into a
   * message, and it is what every other record in the product is addressed by; thirty-six
   * characters of internal identifier is none of those things. It survives a rename, which a
   * name would not.
   *
   * Shape-checked here only — `parseRef` refuses anything that is not `US` followed by digits —
   * and NOT checked against this agency's staff, which is the same call `/login` makes about
   * `redirect`: a ref for somebody who has left simply matches no manager and highlights nobody,
   * which beats a router error page for a link that has gone stale.
   */
  manager?: string;
  /** `suburbs` or `grids` — pins is the default and a default is not written to a URL. */
  view?: Exclude<PropertyMapView, 'pins'>;
  /**
   * Which suburb's figures are open, as the boundary dataset's own id — `?view=suburbs&suburb=1234`.
   *
   * AN ID, where `manager` is a ref, because a suburb has no ref of ours and its name is not
   * unique: there is a Hillcrest in three cities. "Our rents in Hillcrest" is exactly the link
   * one person sends another, so it has to open the same Hillcrest for both of them.
   *
   * Kept only alongside `view=suburbs`. Shape-checked and not checked against the file, which the
   * route has not loaded: an id from before a boundary release simply opens no panel.
   */
  suburb?: number;
  /**
   * Which grid's figures are open — `?view=grids&grid=<id>`. The grid's uuid, as a saved list's
   * URL carries its own: a grid has no ref, and its name can be changed. Kept only alongside
   * `view=grids`; an id for a grid that has since been deleted opens the list of grids.
   */
  grid?: string;
  /**
   * How the suburbs or grids are coloured — `?view=suburbs&colour=heat&metric=rent`.
   *
   * IN THE URL for the reason everything else here is: "our rents by suburb" is a link somebody
   * sends, and it has to open as the heat map of rent it was when they sent it. Kept only
   * alongside a view that has areas to colour, and the standard blue — the default — is not
   * written.
   */
  colour?: Exclude<AreaColouring, 'standard'>;
  /** What the heat map is a map of. Kept only alongside `colour=heat`; the count is the default. */
  metric?: Exclude<HeatMetric, 'properties'>;
}

/** The colouring and, for a heat map, its metric — each only where it means something. */
function areaColouring(search: Record<string, unknown>): Pick<PropertyMapSearch, 'colour' | 'metric'> {
  const { colour, metric } = search;
  if (typeof colour !== 'string' || !isAreaColouring(colour) || colour === 'standard') return {};
  if (colour !== 'heat') return { colour };
  return typeof metric === 'string' && isHeatMetric(metric) && metric !== 'properties'
    ? { colour, metric }
    : { colour };
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A suburb id off the URL: a positive whole number, however the router chose to parse it. */
function suburbId(value: unknown): { suburb?: number } {
  const id = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  return Number.isSafeInteger(id) && id > 0 ? { suburb: id } : {};
}

/**
 * A staff ref in its canonical spelling, or nothing.
 *
 * `parseRef` is deliberately forgiving — it takes `us-3` and `US 3` as readily as `US000003`,
 * because refs get typed by hand and read off phone calls. What goes back in the URL is the one
 * canonical form, so a link is spelled the same way however it was arrived at and two spellings
 * of one manager do not become two different-looking bookmarks.
 */
function staffRef(value: string): { manager?: string } {
  const parsed = parseRef(value);
  return parsed?.entity === 'user' ? { manager: formatRef('user', parsed.sequence) } : {};
}

export const Route = createFileRoute('/properties/map')({
  /*
   * THE MAP'S STATE BELONGS IN THE URL, and it was in `useState`.
   *
   * "Our vacant properties in Hamilton" is a thing one person sends another, and a thing somebody
   * bookmarks and opens every Monday. Held in component state it survived neither, and it did not
   * survive the back button either — an operator who opened a property from a pin and came back
   * got the unfiltered map and had to type the suburb again. The transactions page made this call
   * already and this screen is the same shape.
   *
   * Shape-checked and otherwise forgiving, for the same reason as everywhere else: a stale link
   * with a filter we have since renamed should draw the whole portfolio, not a router error.
   */
  validateSearch: (search: Record<string, unknown>): PropertyMapSearch => ({
    ...(typeof search.q === 'string' && search.q.trim() !== '' ? { q: search.q } : {}),
    ...(typeof search.show === 'string' && isPropertyMapFilter(search.show) && search.show !== 'all'
      ? { show: search.show }
      : {}),
    ...(typeof search.manager === 'string' ? staffRef(search.manager) : {}),
    ...(typeof search.view === 'string' && isPropertyMapView(search.view) && search.view !== 'pins'
      ? {
          view: search.view,
          // Each view keeps only its own selection: a suburb id means nothing to the grid view.
          ...(search.view === 'suburbs' ? suburbId(search.suburb) : {}),
          ...(search.view === 'grids' && typeof search.grid === 'string' && UUID.test(search.grid)
            ? { grid: search.grid }
            : {}),
          ...areaColouring(search),
        }
      : {}),
  }),
  component: PropertiesMapRoute,
});

function PropertiesMapRoute() {
  const { q, show, manager, view, suburb, grid, colour, metric } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  /*
   * THE ROUTE OWNS THE STATE AND THE PAGE IS CONTROLLED, rather than the page reaching for the
   * router. `PropertyMapPage` lives in `features/` and this file imports it lazily; having it
   * import back would close a cycle for no gain. It is also how `TransactionFilters` is driven.
   */
  /*
   * Explicitly `| undefined` on every field rather than reusing `PropertyMapSearch`: under
   * `exactOptionalPropertyTypes` an absent key and a key holding undefined are different types,
   * and CLEARING a filter — picking "Everyone" — is passing undefined for it on purpose.
   */
  const set = (next: { [K in keyof PropertyMapSearch]: PropertyMapSearch[K] | undefined }, replace: boolean) =>
    void navigate({
      search: () => ({
        ...(next.q === undefined || next.q.trim() === '' ? {} : { q: next.q }),
        ...(next.show === undefined || next.show === 'all' ? {} : { show: next.show }),
        ...(next.manager === undefined || next.manager === '' ? {} : { manager: next.manager }),
        // A suburb is only ever open in the suburb view, so leaving the view closes it.
        ...(next.view === undefined
          ? {}
          : {
              view: next.view,
              ...(next.suburb === undefined ? {} : { suburb: next.suburb }),
              ...(next.grid === undefined ? {} : { grid: next.grid }),
              // Through the same check a pasted link gets, so a metric never outlives its heat map.
              ...areaColouring(next),
            }),
      }),
      replace,
    });

  /** Everything except the one being changed, so a filter does not drop the search and vice versa. */
  const rest = {
    ...(q === undefined ? {} : { q }),
    ...(show === undefined ? {} : { show }),
    ...(manager === undefined ? {} : { manager }),
    ...(view === undefined ? {} : { view }),
    ...(suburb === undefined ? {} : { suburb }),
    ...(grid === undefined ? {} : { grid }),
    ...(colour === undefined ? {} : { colour }),
    ...(metric === undefined ? {} : { metric }),
  };

  return (
    <Suspense fallback={<div className="h-full" />}>
      <PropertyMapPage
        query={q ?? ''}
        filter={show ?? 'all'}
        manager={manager}
        view={view ?? 'pins'}
        suburb={suburb}
        grid={grid}
        /*
         * TYPING REPLACES, CHOOSING PUSHES. Nine history entries for one typed suburb makes the
         * back button useless; a filter chip is a decision worth being able to undo. The same
         * split the ledger's filters make.
         */
        onQueryChange={(value) => set({ ...rest, q: value }, true)}
        onFilterChange={(value) => set({ ...rest, show: value }, false)}
        onManagerChange={(value) => set({ ...rest, manager: value }, false)}
        /*
         * Both push. Switching view is a decision in the way a chip is, and so is opening a
         * suburb: back should close the panel that was just opened, not leave the map.
         */
        onViewChange={(value) =>
          set(
            { ...rest, view: value === 'pins' ? undefined : value, suburb: undefined, grid: undefined },
            false,
          )
        }
        onSuburbChange={(value) => set({ ...rest, suburb: value }, false)}
        onGridChange={(value) => set({ ...rest, grid: value }, false)}
        colouring={colour ?? 'standard'}
        metric={metric ?? 'properties'}
        // Both push: a colouring is a decision like a chip is, so back undoes it.
        onColouringChange={(value) =>
          set({ ...rest, colour: value === 'standard' ? undefined : value }, false)
        }
        onMetricChange={(value) =>
          set({ ...rest, metric: value === 'properties' ? undefined : value }, false)
        }
      />
    </Suspense>
  );
}
